// Simplified Invite System API Integration
import { firebaseAuth } from "../../helpers/authentication";

// Get auth token for API calls
const getAuthToken = async () => {
  try {
    const user = firebaseAuth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();

    if (!token) {
      throw new Error("Failed to get authentication token");
    }

    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw new Error("Failed to get authentication token");
  }
};

// Get API URL from environment (must include /api; local fallback for dev)
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || "http://localhost:4000/api";
};

/**
 * Validate an invite UID (referrer's Firebase UID)
 * @param {string} inviteUserId - The referrer's Firebase UID
 * @returns {Promise<{success: boolean, valid: boolean, referrerName?: string}>}
 */
export const validateInviteCode = async (inviteUserId) => {
  try {
    // Basic validation - check if it looks like a Firebase UID
    if (!inviteUserId || typeof inviteUserId !== "string") {
      return {
        success: false,
        valid: false,
        error: "Invalid invite code format",
      };
    }

    // Firebase UIDs are typically 28 characters long
    if (inviteUserId.length < 20 || inviteUserId.length > 30) {
      return {
        success: false,
        valid: false,
        error: "Invalid invite code format",
      };
    }

    // Try to get the referrer's name from Firebase Auth
    try {
      // Import Firebase Admin SDK functions if available
      const { firebaseAuth } = await import("../../helpers/authentication");

      // Try to get user info from Firebase (this might not work in frontend)
      // For now, we'll use a more personalized approach
      const referrerName = await getReferrerName(inviteUserId);

      return {
        success: true,
        valid: true,
        referrerName: referrerName || "a Clumse user",
      };
    } catch (firebaseError) {
      console.log(
        "Could not fetch referrer name from Firebase:",
        firebaseError
      );
      // Fallback to generic name
      return {
        success: true,
        valid: true,
        referrerName: "a Clumse user",
      };
    }

    // TODO: Implement backend validation when API is ready
    // const apiUrl = getApiUrl();
    // const response = await fetch(`${apiUrl}/api/invites/validate/${inviteUserId}`);
    // ... rest of backend validation code
  } catch (error) {
    console.error("Error validating invite code:", error);
    return {
      success: false,
      valid: false,
      error: error.message || "Failed to validate invite code",
    };
  }
};

// Helper function to get referrer name
const getReferrerName = async (inviteUserId) => {
  try {
    // Try to get user info from localStorage or sessionStorage
    // This is a temporary solution until backend is ready
    const storedUsers = localStorage.getItem("clumse_users");
    if (storedUsers) {
      const users = JSON.parse(storedUsers);
      const user = users.find((u) => u.uid === inviteUserId);
      if (user && user.displayName) {
        return user.displayName;
      }
      if (user && user.email) {
        // Extract name from email if no display name
        const emailName = user.email.split("@")[0];
        return emailName.charAt(0).toUpperCase() + emailName.slice(1);
      }
    }

    // Fallback: try to extract from common patterns
    // This is a temporary solution
    return null;
  } catch (error) {
    console.log("Error getting referrer name:", error);
    return null;
  }
};

/**
 * Register a referral after successful signup
 * @param {string} referrerUserId - The referrer's Firebase UID
 * @param {string} newUserEmail - The new user's email
 * @returns {Promise<{success: boolean}>}
 */
export const registerReferral = async (referrerUserId, newUserEmail) => {
  try {
    // For now, just log the referral attempt since backend API may not be ready
    console.log("Referral registration attempt:", {
      referrerUserId,
      newUserEmail,
      timestamp: new Date().toISOString(),
    });

    // Return success for now - the referral will be tracked when backend is implemented
    return {
      success: true,
      message: "Referral will be processed when backend is available",
    };

    // TODO: Implement backend registration when API is ready
    // const authToken = await getAuthToken();
    // const apiUrl = getApiUrl();
    // const response = await fetch(`${apiUrl}/api/invites/use`, {
    //   method: 'POST',
    //   headers: {
    //     'Authorization': `Bearer ${authToken}`,
    //     'Content-Type': 'application/json'
    //   },
    //   body: JSON.stringify({
    //     userId: referrerUserId,
    //     email: newUserEmail
    //   })
    // });
    // ... rest of backend registration code
  } catch (error) {
    console.error("Error registering referral:", error);
    // Don't throw error - let signup continue even if referral registration fails
    return {
      success: false,
      message: "Referral registration failed, but signup can continue",
    };
  }
};

/**
 * Get invite statistics for the current user
 * @returns {Promise<{linkClicks: number, signups: number, pendingPayments: number, completedReferrals: number}>}
 */
export const getInviteStats = async () => {
  try {
    // For now, return mock stats since backend API may not be ready
    console.log(
      "Fetching invite stats - using mock data until backend is available"
    );

    return {
      linkClicks: 0,
      signups: 0,
      pendingPayments: 0,
      completedReferrals: 0,
    };

    // TODO: Implement backend stats when API is ready
    // const authToken = await getAuthToken();
    // const apiUrl = getApiUrl();
    // const response = await fetch(`${apiUrl}/api/invites/stats`, {
    //   method: 'GET',
    //   headers: {
    //     'Authorization': `Bearer ${authToken}`,
    //     'Content-Type': 'application/json'
    //   }
    // });
    // ... rest of backend stats code
  } catch (error) {
    console.error("Error getting invite stats:", error);
    // Return default stats on error
    return {
      linkClicks: 0,
      signups: 0,
      pendingPayments: 0,
      completedReferrals: 0,
    };
  }
};

/**
 * Generate invite link using current user's UID (no backend call needed)
 * @returns {string} The invite URL
 */
export const generateInviteLink = () => {
  const user = firebaseAuth.currentUser;
  if (!user?.uid) {
    throw new Error("User not authenticated");
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/signup?invite=${user.uid}`;
};
